import { useEffect, useRef } from 'react';
import { MapContainer, TileLayer, Marker, Circle, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { GPS_RADIUS_METRES } from '../../lib/workerData';

// Colour meaning matches Today's own GPS badges: green = on-site verified,
// amber = pending/unverified GPS, red = flagged/off-site/no-show.
const STATUS_COLOR = { green: '#2F7A4F', yellow: '#B5730A', red: '#B33A2E' };

// Leaflet's default marker image paths break under CRA's bundler (webpack
// rewrites the asset URLs the plugin hardcodes). Sidestepping the whole
// problem with a plain div/SVG icon instead of PNG marker images — also
// gives us the coloured-by-status pin the brief asks for, which the
// default blue pin can't do anyway.
function pinIcon(color, pulse) {
  return L.divIcon({
    className: 'tc-worker-pin',
    html: `<div style="position:relative;width:26px;height:26px;">
      ${pulse ? `<div style="position:absolute;inset:-6px;border-radius:50%;background:${color};opacity:0.28;animation:tcPulse 1.8s ease-out infinite;"></div>` : ''}
      <div style="position:absolute;inset:0;border-radius:50%;background:${color};border:2px solid #FFFFFF;box-shadow:0 1px 4px rgba(26,43,51,0.45);"></div>
    </div>`,
    iconSize: [26, 26],
    iconAnchor: [13, 13],
    popupAnchor: [0, -14],
  });
}

// Leaflet measures its container once at mount and never re-checks on its
// own, so when the surrounding layout resizes (a column ratio change, a
// sidebar growing once data loads, a browser window resize) the rendered
// tiles keep the old dimensions and leave dead space. Watching the
// container and telling Leaflet to re-measure is the standard fix.
function InvalidateOnResize() {
  const map = useMap();
  useEffect(() => {
    const container = map.getContainer();
    const observer = new ResizeObserver(() => map.invalidateSize());
    observer.observe(container);
    return () => observer.disconnect();
  }, [map]);
  return null;
}

// Fits the view to whatever markers/buildings exist, once on load and again
// if the set of coordinates meaningfully changes — a manager shouldn't have
// to manually pan to see their own crew.
function FitBounds({ points }) {
  const map = useMap();
  const key = points.map(p => `${p[0].toFixed(4)},${p[1].toFixed(4)}`).join('|');
  const prevKey = useRef(null);
  useEffect(() => {
    if (!points.length || prevKey.current === key) return;
    prevKey.current = key;
    if (points.length === 1) {
      map.setView(points[0], 14);
    } else {
      map.fitBounds(L.latLngBounds(points), { padding: [40, 40] });
    }
  }, [key, points, map]);
  return null;
}

export default function CommandMap({ markers, buildings, onSelectWorker }) {
  const plottable = markers.filter(m => m.lat != null && m.lng != null);
  const fitPoints = [
    ...plottable.map(m => [m.lat, m.lng]),
    ...buildings.filter(b => b.lat != null).map(b => [b.lat, b.lng]),
  ];
  const firstWithCoords = buildings.find(b => b.lat != null);
  const fallbackCenter = firstWithCoords ? [firstWithCoords.lat, firstWithCoords.lng] : [47.5271, -52.8840];

  return (
    <div style={{ position: 'relative', width: '100%', height: '100%', borderRadius: 12, overflow: 'hidden' }}>
      <style>{`
        @keyframes tcPulse { 0% { transform: scale(0.7); opacity: 0.35; } 100% { transform: scale(1.9); opacity: 0; } }
        .leaflet-popup-content-wrapper { background: #FFFFFF; color: #1A2B33; border-radius: 8px; border: 1px solid #E4E0D8; }
        .leaflet-popup-tip { background: #FFFFFF; }
        .leaflet-container { font-family: 'Segoe UI', system-ui, sans-serif; }
      `}</style>
      <MapContainer center={fallbackCenter} zoom={13} style={{ width: '100%', height: '100%', background: '#F5F3EF' }} scrollWheelZoom={true}>
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        <FitBounds points={fitPoints} />
        <InvalidateOnResize />

        {buildings.filter(b => b.lat != null && b.lng != null).map(building => (
          <Circle
            key={building.id}
            center={[building.lat, building.lng]}
            radius={GPS_RADIUS_METRES}
            pathOptions={{ color: '#8B98A0', weight: 1.5, fillColor: '#8B98A0', fillOpacity: 0.08, dashArray: '4 5' }}
          />
        ))}

        {plottable.map(m => (
          <Marker
            key={m.workerId + (m.subId || 'noshow')}
            position={[m.lat, m.lng]}
            icon={pinIcon(STATUS_COLOR[m.status] || '#5B6B72', m.status === 'red')}
            eventHandlers={{ click: () => onSelectWorker(m.workerId) }}
          >
            <Popup>
              <div style={{ fontWeight: 700, marginBottom: 2 }}>{m.name}</div>
              <div style={{ fontSize: 12, color: '#5B6B72' }}>{m.buildingName}</div>
              <div style={{ fontSize: 12, marginTop: 4, color: '#1A2B33' }}>
                {m.noShow ? 'No-show — not checked in' : m.status === 'red' ? `Off-site${m.distance != null ? ` (~${m.distance}m)` : ''}` : m.status === 'yellow' ? 'No GPS reading' : 'On-site, verified'}
              </div>
              <button
                onClick={() => onSelectWorker(m.workerId)}
                style={{ marginTop: 8, background: '#1F3A47', color: '#FFFFFF', border: 'none', borderRadius: 6, padding: '5px 10px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}
              >
                View details
              </button>
            </Popup>
          </Marker>
        ))}
      </MapContainer>

      <div style={styles.legend}>
        <LegendItem color={STATUS_COLOR.green} label="On-site, verified" />
        <LegendItem color={STATUS_COLOR.yellow} label="Unverified GPS" />
        <LegendItem color={STATUS_COLOR.red} label="Off-site / no-show" />
      </div>
    </div>
  );
}

function LegendItem({ color, label }) {
  return (
    <div style={styles.legendRow}>
      <span style={{ ...styles.legendDot, background: color }} />
      <span>{label}</span>
    </div>
  );
}

const styles = {
  legend: {
    position: 'absolute', bottom: 12, left: 12, zIndex: 500, background: '#FFFFFF',
    border: '1px solid #E4E0D8', borderRadius: 8, padding: '8px 12px', display: 'flex', flexDirection: 'column', gap: 5,
    fontSize: 11.5, color: '#1A2B33', fontFamily: "'Segoe UI', system-ui, sans-serif",
  },
  legendRow: { display: 'flex', alignItems: 'center', gap: 7 },
  legendDot: { width: 9, height: 9, borderRadius: '50%', flexShrink: 0 },
};
